/**
 * DataOutputStream / DataInputStream over a byte buffer, persisted to localStorage.
 *
 * The original saves four binary .dat files with big-endian primitives. Reproducing that
 * exact wire format (rather than swapping in JSON) means Profile/Achievements/Settings/
 * DailyChallenge port field-for-field with no rewrite of their load/save logic — and a
 * version-tagged binary blob keeps their forward/backward-compat branches meaningful.
 */

const PREFIX = 'dotch:';

export class EOFException extends Error {
  constructor() {
    super('EOF');
    this.name = 'EOFException';
  }
}

export class IOException extends Error {
  constructor(msg = 'IO error') {
    super(msg);
    this.name = 'IOException';
  }
}

// ---------------------------------------------------------------------------
// Output
// ---------------------------------------------------------------------------

export class DataOutputStream {
  private bytes: number[] = [];
  private scratch = new DataView(new ArrayBuffer(8));

  constructor(private readonly fileName: string) {}

  writeByte(v: number): void {
    this.bytes.push(v & 0xff);
  }

  writeBoolean(v: boolean): void {
    this.bytes.push(v ? 1 : 0);
  }

  writeShort(v: number): void {
    this.bytes.push((v >>> 8) & 0xff, v & 0xff);
  }

  writeInt(v: number): void {
    const n = v | 0;
    this.bytes.push((n >>> 24) & 0xff, (n >>> 16) & 0xff, (n >>> 8) & 0xff, n & 0xff);
  }

  writeLong(v: number | bigint): void {
    const b = BigInt.asUintN(64, typeof v === 'bigint' ? v : BigInt(Math.trunc(v)));
    for (let i = 7; i >= 0; i--) {
      this.bytes.push(Number((b >> BigInt(i * 8)) & 0xffn));
    }
  }

  writeFloat(v: number): void {
    this.scratch.setFloat32(0, v, false);
    for (let i = 0; i < 4; i++) this.bytes.push(this.scratch.getUint8(i));
  }

  writeDouble(v: number): void {
    this.scratch.setFloat64(0, v, false);
    for (let i = 0; i < 8; i++) this.bytes.push(this.scratch.getUint8(i));
  }

  /** Java modified-UTF8: 2-byte unsigned length prefix, then the encoded bytes. */
  writeUTF(s: string): void {
    const out: number[] = [];
    for (let i = 0; i < s.length; i++) {
      const c = s.charCodeAt(i);
      if (c >= 0x0001 && c <= 0x007f) {
        out.push(c);
      } else if (c <= 0x07ff) {
        out.push(0xc0 | (c >> 6), 0x80 | (c & 0x3f));
      } else {
        out.push(0xe0 | (c >> 12), 0x80 | ((c >> 6) & 0x3f), 0x80 | (c & 0x3f));
      }
    }
    if (out.length > 0xffff) throw new IOException('UTF string too long');
    this.writeShort(out.length);
    for (const b of out) this.bytes.push(b);
  }

  /** Commit to localStorage. Mirrors closing the FileOutputStream. */
  close(): void {
    try {
      localStorage.setItem(PREFIX + this.fileName, bytesToBase64(this.bytes));
    } catch {
      // Quota exceeded / private mode — the original also fails silently on IOException.
    }
  }
}

// ---------------------------------------------------------------------------
// Input
// ---------------------------------------------------------------------------

export class DataInputStream {
  private pos = 0;
  private readonly bytes: Uint8Array;
  private readonly view: DataView;

  constructor(bytes: Uint8Array) {
    this.bytes = bytes;
    this.view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  }

  private need(n: number): number {
    if (this.pos + n > this.bytes.length) throw new EOFException();
    const p = this.pos;
    this.pos += n;
    return p;
  }

  readByte(): number {
    const p = this.need(1);
    return this.view.getInt8(p);
  }

  readUnsignedByte(): number {
    const p = this.need(1);
    return this.view.getUint8(p);
  }

  readBoolean(): boolean {
    return this.readUnsignedByte() !== 0;
  }

  readShort(): number {
    const p = this.need(2);
    return this.view.getInt16(p, false);
  }

  readUnsignedShort(): number {
    const p = this.need(2);
    return this.view.getUint16(p, false);
  }

  readInt(): number {
    const p = this.need(4);
    return this.view.getInt32(p, false);
  }

  readLong(): bigint {
    const p = this.need(8);
    return this.view.getBigInt64(p, false);
  }

  readFloat(): number {
    const p = this.need(4);
    return this.view.getFloat32(p, false);
  }

  readDouble(): number {
    const p = this.need(8);
    return this.view.getFloat64(p, false);
  }

  readUTF(): string {
    const len = this.readUnsignedShort();
    const p = this.need(len);
    let s = '';
    let i = p;
    const end = p + len;
    while (i < end) {
      const a = this.bytes[i];
      if (a < 0x80) {
        s += String.fromCharCode(a);
        i += 1;
      } else if ((a & 0xe0) === 0xc0) {
        const b = this.bytes[i + 1];
        s += String.fromCharCode(((a & 0x1f) << 6) | (b & 0x3f));
        i += 2;
      } else {
        const b = this.bytes[i + 1];
        const c = this.bytes[i + 2];
        s += String.fromCharCode(((a & 0x0f) << 12) | ((b & 0x3f) << 6) | (c & 0x3f));
        i += 3;
      }
    }
    return s;
  }

  available(): number {
    return this.bytes.length - this.pos;
  }

  close(): void { /* nothing to release */ }
}

// ---------------------------------------------------------------------------
// "Filesystem" — localStorage-backed
// ---------------------------------------------------------------------------

export const FileSystem = {
  /** Equivalent of `new File(name).exists()`. */
  exists(fileName: string): boolean {
    try {
      return localStorage.getItem(PREFIX + fileName) !== null;
    } catch {
      return false;
    }
  },

  /** Open for reading, or null when the file does not exist / is unreadable. */
  open(fileName: string): DataInputStream | null {
    try {
      const raw = localStorage.getItem(PREFIX + fileName);
      if (raw === null) return null;
      return new DataInputStream(base64ToBytes(raw));
    } catch {
      return null;
    }
  },

  delete(fileName: string): void {
    try {
      localStorage.removeItem(PREFIX + fileName);
    } catch {
      // ignore
    }
  },
};

// ---------------------------------------------------------------------------
// base64
// ---------------------------------------------------------------------------

function bytesToBase64(bytes: number[]): string {
  let binary = '';
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode.apply(null, bytes.slice(i, i + CHUNK) as unknown as number[]);
  }
  return btoa(binary);
}

function base64ToBytes(b64: string): Uint8Array {
  const binary = atob(b64);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
  return out;
}
